import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  ExternalLink,
  Clock,
  FileCheck2,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { api } from "@/lib/api";
import { GMAT_FOCUS_STRUCTURE } from "@/lib/gmat-mock-exam";
import { AgentRunPanel } from "./AgentRunPanel";
import { cn } from "@/lib/cn";

export function MockExamHub({
  onStartCatalog,
  onStartAgentic,
}: {
  onStartCatalog: (examId: string) => void;
  onStartAgentic: (sessionId: string) => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["exam-catalog"],
    queryFn: () => api.examCatalog(),
  });

  return (
    <div className="container space-y-16 pb-20 pt-10">
      <header>
        <div className="flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-muted-foreground">
          <span className="hero-rule" /> Mock exams
        </div>
        <h1 className="mt-4 font-serif text-5xl leading-[0.98] tracking-tight md:text-7xl">
          Test-day <span className="italic text-primary">rehearsal</span>.
        </h1>
        <p className="mt-4 max-w-2xl text-muted-foreground">
          Take GMAC's own practice exams for the most authentic score estimate.
          Between mocks, use Think Big Techies' timed simulations to hold pace and format —
          clearly labeled as practice, never mistaken for official items.
        </p>
      </header>

      <AgentRunPanel onStart={onStartAgentic} />

      <Card className="grain border-primary/25 bg-gradient-to-br from-background via-card to-primary/5 shadow-editorial">
        <CardContent className="flex flex-col gap-4 p-6 md:flex-row md:items-center md:justify-between md:p-8">
          <div className="flex gap-3">
            <ShieldCheck className="mt-0.5 size-5 shrink-0 text-primary" />
            <div>
              <p className="font-serif text-xl">GMAT Focus Edition — the real thing</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {GMAT_FOCUS_STRUCTURE.sections
                  .map((s) => `${s.questions} ${s.label.split(" ")[0]}`)
                  .join(" · ")}{" "}
                · {GMAT_FOCUS_STRUCTURE.totalMinutes} min total · optional{" "}
                {GMAT_FOCUS_STRUCTURE.optionalBreakMinutes}-min break
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <section className="space-y-6">
        <div className="flex items-center gap-3">
          <FileCheck2 className="size-5 text-primary" />
          <h2 className="font-serif text-3xl">Official GMAC exams</h2>
          <Badge variant="secondary" className="rounded-full">
            Authentic · external
          </Badge>
        </div>
        <p className="max-w-2xl text-sm text-muted-foreground">
          Real GMAT questions, official scoring, same timing as test day. You
          complete these on mba.com — we hand you off there.
        </p>
        {isLoading && <p className="text-sm text-muted-foreground">Loading catalog…</p>}
        {error && (
          <p className="text-sm text-destructive">
            Could not load exam catalog. Start the API locally or check deployment.
          </p>
        )}
        <div className="grid gap-4 md:grid-cols-2">
          {data?.official.map((exam, i) => (
            <motion.div
              key={exam.id}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
            >
              <Card className="flex h-full flex-col border-border/60 bg-card/70 shadow-editorial transition-all hover:-translate-y-0.5 hover:shadow-editorial-lg">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="font-serif text-xl leading-snug">
                      {exam.name}
                    </CardTitle>
                    <Badge variant={exam.price === "Free" ? "default" : "outline"}>
                      {exam.price}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">{exam.questions}</p>
                </CardHeader>
                <CardContent className="mt-auto space-y-4">
                  <p className="text-sm text-muted-foreground">{exam.description}</p>
                  <a href={exam.url} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" className="w-full">
                      Open on mba.com
                      <ExternalLink className="size-4" />
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="space-y-6">
        <div className="flex items-center gap-3">
          <Sparkles className="size-5 text-accent" />
          <h2 className="font-serif text-3xl">Think Big Techies simulations</h2>
          <Badge variant="outline" className="rounded-full">
            In-app
          </Badge>
        </div>
        <p className="max-w-2xl text-sm text-muted-foreground">
          Original GMAT Focus–format items with section timers and score breakdown.
          Best for weekly pacing — pair with an official GMAC mock before test day.
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          {data?.sigma.map((exam, i) => (
            <motion.div
              key={exam.id}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
            >
              <Card
                className={cn(
                  "flex h-full flex-col border-border/60 bg-card/70 shadow-editorial transition-all hover:-translate-y-0.5 hover:shadow-editorial-lg",
                  exam.id === "sigma-mini" && "border-accent/40 ring-1 ring-accent/20",
                )}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="font-serif text-xl leading-snug">
                      {exam.title}
                    </CardTitle>
                    {exam.id === "sigma-mini" && (
                      <Badge className="bg-accent/15 text-accent-foreground">Start here</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{exam.subtitle}</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Clock className="size-3" />
                      {exam.total_minutes} min
                    </span>
                    <span>·</span>
                    <span>{exam.total_questions} questions</span>
                    <span>·</span>
                    <span>
                      {exam.sections.length} section{exam.sections.length > 1 ? "s" : ""}
                    </span>
                  </div>
                  <ul className="space-y-1 text-xs text-muted-foreground">
                    {exam.sections.map((s) => (
                      <li key={s.id} className="flex items-center gap-1.5">
                        <span className="size-1 rounded-full bg-muted-foreground/50" />
                        {s.label}: {s.question_count} Q / {s.minutes} min
                      </li>
                    ))}
                  </ul>
                  <Button
                    variant={exam.id === "sigma-mini" ? "default" : "outline"}
                    className="w-full"
                    onClick={() => onStartCatalog(exam.id)}
                  >
                    Start exam
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
